<template>
  <v-container fluid class="ncrm-import-details">
    <v-card class="pa-6">
      <div class="d-flex flex-wrap align-start justify-space-between ga-4">
        <div>
          <h2 class="text-h5 ncrm-text-strong">
            {{ $t("ncrm.importDetails.title", { id: batchId }) }}
          </h2>
          <div class="text-body-2 text-medium-emphasis">
            {{ batch.import_name || batch.file_name || "-" }}
          </div>
        </div>
        <div class="d-flex flex-wrap ga-2">
          <v-btn variant="outlined" @click="goBack">
            <v-icon start>mdi-arrow-left</v-icon>
            {{ $t("ncrm.importDetails.back") }}
          </v-btn>
          <v-btn
            color="error"
            variant="outlined"
            :disabled="!canRollback"
            :loading="rollbackDialog.loading"
            @click="openRollbackDialog"
          >
            <v-icon start>mdi-undo-variant</v-icon>
            {{ $t("ncrm.importDetails.rollback") }}
          </v-btn>
          <v-btn color="primary" variant="tonal" :disabled="!hasErrors" @click="downloadErrorsCsv">
            <v-icon start>mdi-file-delimited-outline</v-icon>
            {{ $t("ncrm.importDetails.downloadErrors") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-row class="mt-2">
      <v-col cols="12" md="3">
        <v-card class="pa-4">
          <div class="text-caption text-medium-emphasis">{{ $t("ncrm.importDetails.cards.total") }}</div>
          <div class="text-h6 mt-1">{{ batch.total_rows || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4">
          <div class="text-caption text-medium-emphasis">{{ $t("ncrm.importDetails.cards.success") }}</div>
          <div class="text-h6 mt-1">{{ batch.success_count || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4">
          <div class="text-caption text-medium-emphasis">{{ $t("ncrm.importDetails.cards.errors") }}</div>
          <div class="text-h6 mt-1">{{ batch.error_count || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4">
          <div class="text-caption text-medium-emphasis">{{ $t("ncrm.importDetails.cards.conflicts") }}</div>
          <div class="text-h6 mt-1">{{ batch.conflict_count || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4">
          <div class="text-caption text-medium-emphasis">{{ $t("ncrm.importDetails.cards.status") }}</div>
          <v-chip
            size="small"
            :color="statusColor(batch.status)"
            :variant="statusVariant(batch.status)"
            class="mt-2"
          >
            {{ formatStatus(batch.status) }}
          </v-chip>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="mt-4">
      <v-tabs v-model="activeTab" color="primary">
        <v-tab value="contacts">
          {{
            isActivityImport
              ? $t("ncrm.importDetails.tabs.messages")
              : $t("ncrm.importDetails.tabs.contacts")
          }}
        </v-tab>
        <v-tab v-if="showsUnlinkedTab" value="unlinked">
          {{
            isActivityImport
              ? $t("ncrm.importDetails.tabs.unlinkedActivity")
              : $t("ncrm.importDetails.tabs.unlinked")
          }}
        </v-tab>
        <v-tab value="conflicts">{{ $t("ncrm.importDetails.tabs.conflicts") }}</v-tab>
        <v-tab value="errors">{{ $t("ncrm.importDetails.tabs.errors") }}</v-tab>
      </v-tabs>
      <v-divider />

      <v-card-text v-if="activeTab === 'contacts'">
        <v-data-table-server
          :headers="contactHeaders"
          :items="contactRows"
          :items-length="contactTotal"
          :loading="rowsLoading"
          v-model:page="contactPage"
          v-model:items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          item-key="id"
          density="comfortable"
          class="elevation-0 ncrm-import-details__table"
        >
          <template #item.status="{ item }">
            <v-chip size="small" :color="statusColor(item.status)" :variant="statusVariant(item.status)">
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>
          <template #item.name="{ item }">
            <span>{{ resolveDisplayName(item) }}</span>
          </template>
          <template #item.email="{ item }">
            <span>{{ resolveMappedField(item, "email_primary") }}</span>
          </template>
          <template #item.phone="{ item }">
            <span>{{ resolveMappedField(item, "phone_primary") }}</span>
          </template>
          <template #item.content="{ item }">
            <span>{{ resolveActivityContent(item) }}</span>
          </template>
          <template #item.entity="{ item }">
            <span>{{ resolveActivityEntity(item) }}</span>
          </template>
          <template #item.dedupe="{ item }">
            <span>{{ resolveActivityDedupe(item) }}</span>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.importDetails.emptyContacts") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>

      <v-card-text v-else-if="activeTab === 'unlinked'">
        <div class="d-flex justify-end mb-3">
          <v-text-field
            v-model="unlinkedSearch"
            :label="$t('ncrm.importDetails.searchUnlinked')"
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="ncrm-unlinked-search"
          />
        </div>
        <v-data-table-server
          v-if="!isActivityImport"
          :headers="unlinkedHeaders"
          :items="unlinkedRows"
          :items-length="unlinkedTotal"
          :loading="rowsLoading"
          v-model:page="unlinkedPage"
          v-model:items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          item-key="personId"
          density="comfortable"
          class="elevation-0 ncrm-import-details__table"
        >
          <template #item.company="{ item }">
            <v-autocomplete
              v-model="item.selectedCompanyId"
              :items="companyOptions"
              item-title="title"
              item-value="value"
              :label="$t('ncrm.importDetails.selectCompany')"
              :loading="companyOptionsLoading"
              density="compact"
              hide-details
              clearable
            />
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex ga-2">
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                :disabled="!item.selectedCompanyId"
                @click="linkUnlinkedPerson(item)"
              >
                {{ $t("ncrm.importDetails.link") }}
              </v-btn>
              <v-btn size="small" variant="tonal" @click="openCreateCompanyDialog(item)">
                {{ $t("ncrm.importDetails.createCompany") }}
              </v-btn>
              <v-btn size="small" variant="text" @click="skipUnlinkedPerson(item)">
                {{ $t("ncrm.importDetails.skip") }}
              </v-btn>
            </div>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.importDetails.emptyUnlinked") }}
            </v-alert>
          </template>
        </v-data-table-server>

        <v-data-table-server
          v-else
          :headers="activityUnlinkedHeaders"
          :items="unlinkedRows"
          :items-length="unlinkedTotal"
          :loading="rowsLoading"
          v-model:page="unlinkedPage"
          v-model:items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          item-key="rowId"
          density="comfortable"
          class="elevation-0 ncrm-import-details__table"
        >
          <template #item.entity="{ item }">
            <v-autocomplete
              v-model="item.selectedEntityId"
              :items="activityEntityOptions"
              item-title="title"
              item-value="value"
              :label="activityEntityLabel"
              :loading="activityEntityOptionsLoading"
              density="compact"
              hide-details
              clearable
            />
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex ga-2">
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                :disabled="!item.selectedEntityId"
                @click="assignUnlinkedActivity(item)"
              >
                {{ $t("ncrm.importDetails.assign") }}
              </v-btn>
              <v-btn size="small" variant="text" @click="skipUnlinkedActivity(item)">
                {{ $t("ncrm.importDetails.skip") }}
              </v-btn>
            </div>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.importDetails.emptyUnlinkedActivity") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>

      <v-card-text v-else-if="activeTab === 'conflicts'">
        <div class="d-flex flex-wrap align-center justify-space-between mb-4 ga-3">
          <div class="text-body-2 text-medium-emphasis">
            {{
              $t("ncrm.importDetails.bulkResolveHint", {
                selected: selectedConflictCount,
                total: conflictTotal,
              })
            }}
          </div>
          <div class="d-flex flex-wrap align-center ga-2">
            <v-select
              v-model="bulkConflictDecision"
              :items="decisionOptions"
              item-title="title"
              item-value="value"
              :label="$t('ncrm.importDetails.bulkResolveDecision')"
              density="compact"
              hide-details
              class="ncrm-import-details__bulk-select"
              :disabled="rowsLoading || bulkResolveLoading || !conflictTotal"
            />
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :loading="bulkResolveLoading && bulkResolveScope === 'selected'"
              :disabled="rowsLoading || bulkResolveLoading || !selectedConflictCount"
              @click="applyBulkResolveSelected"
            >
              {{ $t("ncrm.importDetails.applyResolveSelected") }}
            </v-btn>
            <v-btn
              size="small"
              variant="outlined"
              color="primary"
              :loading="bulkResolveLoading && bulkResolveScope === 'all'"
              :disabled="rowsLoading || bulkResolveLoading || !conflictTotal"
              @click="applyBulkResolveAllConflicts"
            >
              {{ $t("ncrm.importDetails.applyResolveAllConflicts") }}
            </v-btn>
            <v-btn
              size="small"
              variant="text"
              :disabled="bulkResolveLoading || !selectedConflictCount"
              @click="clearConflictSelection"
            >
              {{ $t("ncrm.importDetails.clearSelection") }}
            </v-btn>
          </div>
        </div>
        <v-data-table-server
          :headers="conflictHeaders"
          :items="conflictRows"
          :items-length="conflictTotal"
          :loading="rowsLoading"
          v-model:page="conflictPage"
          v-model:items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          item-key="id"
          density="comfortable"
          class="elevation-0 ncrm-import-details__table"
        >
          <template #header.select>
            <v-checkbox-btn
              :model-value="allConflictRowsOnPageSelected"
              :indeterminate="someConflictRowsOnPageSelected"
              :disabled="rowsLoading || bulkResolveLoading || !conflictRows.length"
              @update:model-value="toggleSelectAllConflictsOnPage"
            />
          </template>
          <template #item.select="{ item }">
            <v-checkbox-btn
              :model-value="isConflictSelected(item?.id)"
              :disabled="bulkResolveLoading"
              @update:model-value="setConflictSelected(item?.id, $event)"
            />
          </template>
          <template #item.status="{ item }">
            <v-chip size="small" :color="statusColor(item.status)" :variant="statusVariant(item.status)">
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>
          <template #item.name="{ item }">
            <span>{{ resolveDisplayName(item) }}</span>
          </template>
          <template #item.fields="{ item }">
            <span>{{ item?.rawData?.conflictFields?.length || 0 }}</span>
          </template>
          <template #item.actions="{ item }">
            <v-btn size="small" variant="tonal" color="primary" @click="openResolveDialog(item)">
              {{ $t("ncrm.importDetails.resolve") }}
            </v-btn>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.importDetails.emptyConflicts") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>

      <v-card-text v-else>
        <v-data-table-server
          :headers="errorHeaders"
          :items="errorRows"
          :items-length="errorTotal"
          :loading="rowsLoading"
          v-model:page="errorPage"
          v-model:items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          item-key="id"
          density="comfortable"
          class="elevation-0 ncrm-import-details__table"
        >
          <template #item.errorMessage="{ item }">
            <span class="text-error">{{ item.errorMessage }}</span>
          </template>
          <template #item.raw="{ item }">
            <span>{{ stringifyRaw(item.rawData?.raw || item.rawData) }}</span>
          </template>
          <template #item.actions="{ item }">
            <v-btn size="small" variant="tonal" color="primary" @click="openErrorResolveDialog(item)">
              {{ $t("ncrm.importDetails.resolve") }}
            </v-btn>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.importDetails.emptyErrors") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <v-dialog v-model="resolveDialog.show" max-width="920">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="text-h6">{{ $t("ncrm.importDetails.resolveTitle") }}</div>
          <v-btn icon @click="closeResolveDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="resolveDialog.entityContext" class="text-body-1 mb-1 ncrm-import-details__entity-summary">
            <strong>{{ resolveDialog.entityContext.existingLabel }}</strong>
            <span>{{ resolveDialog.entityContext.existingName }}</span>
          </div>
          <div
            v-if="resolveDialog.entityContext && resolveDialog.entityContext.showImported"
            class="text-body-2 mb-2 ncrm-import-details__entity-summary-secondary"
          >
            <strong>{{ resolveDialog.entityContext.importedLabel }}</strong>
            <span>{{ resolveDialog.entityContext.importedName }}</span>
          </div>
          <div v-if="resolveDialog.matchSummary" class="text-body-2 mb-3 ncrm-import-details__match-summary">
            {{ resolveDialog.matchSummary }}
          </div>
          <v-data-table
            :headers="resolveHeaders"
            :items="resolveDialog.fields"
            class="elevation-0 ncrm-import-details__table"
            density="comfortable"
            item-key="field"
          >
            <template #item.field="{ item }">
              <span>{{ humanizeField(item.field) }}</span>
            </template>
            <template #item.existingValue="{ item }">
              <span>{{ formatCellValue(item.existingValue) }}</span>
            </template>
            <template #item.incomingValue="{ item }">
              <span>{{ formatCellValue(item.incomingValue) }}</span>
            </template>
            <template #item.decision="{ item }">
              <v-select
                v-model="resolveDialog.decisions[item.field]"
                :items="decisionOptions"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
              />
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="closeResolveDialog">
            {{ $t("ncrm.importDetails.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="resolveDialog.loading" @click="submitResolve">
            {{ $t("ncrm.importDetails.applyResolve") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="errorResolveDialog.show" max-width="980">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="text-h6">{{ $t("ncrm.importDetails.errorResolveTitle") }}</div>
          <v-btn icon @click="closeErrorResolveDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="errorResolveDialog.entitySummary" class="text-body-1 mb-2 ncrm-import-details__entity-summary">
            <strong>{{ errorResolveDialog.entitySummary.label }}</strong>
            <span>{{ errorResolveDialog.entitySummary.value }}</span>
          </div>

          <v-alert type="info" class="mb-3">
            {{ $t("ncrm.importDetails.errorResolveHint") }}
          </v-alert>

          <div class="text-body-2 mb-1">
            <strong>{{ $t("ncrm.importDetails.headers.row") }}:</strong>
            <span>{{ errorResolveDialog.rowNumber || "-" }}</span>
          </div>
          <div class="text-body-2 mb-3">
            <strong>{{ $t("ncrm.importDetails.headers.error") }}:</strong>
            <span class="text-error">{{ errorResolveDialog.errorMessage || "-" }}</span>
          </div>

          <div class="text-subtitle-2 mb-2">{{ $t("ncrm.importDetails.errorResolveMappedSection") }}</div>
          <v-data-table
            :headers="errorResolveHeaders"
            :items="errorResolveDialog.mappedFields"
            class="elevation-0 mb-4 ncrm-import-details__table"
            density="comfortable"
            item-key="field"
          >
            <template #item.field="{ item }">
              <span>{{ humanizeField(item.field) }}</span>
            </template>
            <template #item.value="{ item }">
              <v-text-field
                v-model="item.value"
                density="compact"
                hide-details
                :disabled="errorResolveDialog.loading"
              />
            </template>
            <template #no-data>
              <div class="text-body-2 text-medium-emphasis pa-4">
                {{ $t("ncrm.importDetails.errorResolveNoMapped") }}
              </div>
            </template>
          </v-data-table>

          <div class="text-subtitle-2 mb-2">{{ $t("ncrm.importDetails.errorResolveRawSection") }}</div>
          <v-sheet variant="outlined" rounded class="pa-3 ncrm-import-details__raw-preview">
            <pre class="ncrm-import-details__raw-pre">{{ errorResolveDialog.rawPreview || "-" }}</pre>
          </v-sheet>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="closeErrorResolveDialog">
            {{ $t("ncrm.importDetails.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="errorResolveDialog.loading" @click="submitErrorRetry">
            {{ $t("ncrm.importDetails.retryRow") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createCompanyDialog.show" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="text-h6">{{ $t("ncrm.importDetails.createCompanyTitle") }}</div>
          <v-btn icon @click="closeCreateCompanyDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="createCompanyDialog.name"
            :label="$t('ncrm.importDetails.companyName')"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="closeCreateCompanyDialog">
            {{ $t("ncrm.importDetails.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="createCompanyDialog.loading" @click="createCompanyForPerson">
            {{ $t("ncrm.importDetails.createCompany") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rollbackDialog.show" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="text-h6">{{ $t("ncrm.importDetails.rollbackTitle") }}</div>
          <v-btn icon @click="closeRollbackDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <p class="text-body-2 mb-3">{{ $t("ncrm.importDetails.rollbackHint") }}</p>
          <v-text-field
            v-model="rollbackDialog.confirmText"
            :label="$t('ncrm.importDetails.confirmDelete')"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="closeRollbackDialog">
            {{ $t("ncrm.importDetails.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="rollbackDialog.loading" @click="submitRollback">
            {{ $t("ncrm.importDetails.rollback") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="3500" color="info">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmImportDetails",
  data() {
    return {
      batch: {},
      activeTab: "contacts",
      itemsPerPage: 25,
      itemsPerPageOptions: [25, 50, 100],
      contactRows: [],
      unlinkedRows: [],
      conflictRows: [],
      errorRows: [],
      contactTotal: 0,
      unlinkedTotal: 0,
      conflictTotal: 0,
      errorTotal: 0,
      contactPage: 1,
      unlinkedPage: 1,
      conflictPage: 1,
      errorPage: 1,
      unlinkedSearch: "",
      selectedConflictRowIds: [],
      bulkConflictDecision: "incoming",
      bulkResolveLoading: false,
      bulkResolveScope: "",
      companyOptions: [],
      companyOptionsLoading: false,
      createCompanyDialog: {
        show: false,
        loading: false,
        personId: null,
        name: "",
      },
      activityEntityOptions: [],
      activityEntityOptionsLoading: false,
      rollbackDialog: {
        show: false,
        loading: false,
        confirmText: "",
      },
      rowsLoading: false,
      resolveDialog: {
        show: false,
        loading: false,
        rowId: null,
        fields: [],
        decisions: {},
        entityContext: null,
        matchSummary: "",
      },
      errorResolveDialog: {
        show: false,
        loading: false,
        rowId: null,
        rowNumber: null,
        errorMessage: "",
        entitySummary: null,
        mappedFields: [],
        rawPreview: "",
      },
      snackbar: {
        show: false,
        message: "",
      },
    };
  },
  computed: {
    batchId() {
      return Number(this.$route.params.batchId);
    },
    hasErrors() {
      return Number(this.batch?.error_count || 0) > 0;
    },
    isActivityImport() {
      return String(this.batch?.import_type || "").toLowerCase() === "activity";
    },
    showsUnlinkedTab() {
      const type = String(this.batch?.import_type || "").toLowerCase();
      return type === "person" || type === "activity";
    },
    canRollback() {
      const status = String(this.batch?.status || "").toLowerCase();
      return ["completed", "completed_with_errors", "completed_with_conflicts"].includes(status);
    },
    contactHeaders() {
      if (this.isActivityImport) {
        return [
          { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
          { title: this.$t("ncrm.importDetails.headers.status"), key: "status" },
          { title: this.$t("ncrm.importDetails.headers.content"), key: "content" },
          { title: this.$t("ncrm.importDetails.headers.entity"), key: "entity" },
          { title: this.$t("ncrm.importDetails.headers.dedupe"), key: "dedupe" },
        ];
      }
      return [
        { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
        { title: this.$t("ncrm.importDetails.headers.status"), key: "status" },
        { title: this.$t("ncrm.importDetails.headers.name"), key: "name" },
        { title: this.$t("ncrm.importDetails.headers.email"), key: "email" },
        { title: this.$t("ncrm.importDetails.headers.phone"), key: "phone" },
      ];
    },
    conflictHeaders() {
      return [
        { title: "", key: "select", sortable: false, width: 52 },
        { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
        { title: this.$t("ncrm.importDetails.headers.status"), key: "status" },
        { title: this.$t("ncrm.importDetails.headers.name"), key: "name" },
        { title: this.$t("ncrm.importDetails.headers.fields"), key: "fields" },
        { title: this.$t("ncrm.importDetails.headers.actions"), key: "actions", sortable: false },
      ];
    },
    selectedConflictCount() {
      return Array.isArray(this.selectedConflictRowIds) ? this.selectedConflictRowIds.length : 0;
    },
    conflictPageRowIds() {
      return (Array.isArray(this.conflictRows) ? this.conflictRows : [])
        .map((row) => Number(row?.id))
        .filter((value) => Number.isInteger(value) && value > 0);
    },
    allConflictRowsOnPageSelected() {
      if (!this.conflictPageRowIds.length) return false;
      const selected = new Set(this.selectedConflictRowIds.map((value) => Number(value)));
      return this.conflictPageRowIds.every((id) => selected.has(id));
    },
    someConflictRowsOnPageSelected() {
      if (!this.conflictPageRowIds.length) return false;
      const selected = new Set(this.selectedConflictRowIds.map((value) => Number(value)));
      const selectedOnPageCount = this.conflictPageRowIds.filter((id) => selected.has(id)).length;
      return selectedOnPageCount > 0 && selectedOnPageCount < this.conflictPageRowIds.length;
    },
    unlinkedHeaders() {
      return [
        { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
        { title: this.$t("ncrm.importDetails.headers.name"), key: "name" },
        { title: this.$t("ncrm.importDetails.headers.email"), key: "email" },
        { title: this.$t("ncrm.importDetails.headers.phone"), key: "phone" },
        { title: this.$t("ncrm.importDetails.headers.company"), key: "company", sortable: false },
        { title: this.$t("ncrm.importDetails.headers.actions"), key: "actions", sortable: false },
      ];
    },
    activityUnlinkedHeaders() {
      return [
        { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
        { title: this.$t("ncrm.importDetails.headers.content"), key: "content" },
        { title: this.$t("ncrm.importDetails.headers.matchValue"), key: "matchValue" },
        { title: this.$t("ncrm.importDetails.headers.entity"), key: "entity", sortable: false },
        { title: this.$t("ncrm.importDetails.headers.actions"), key: "actions", sortable: false },
      ];
    },
    activityEntityType() {
      const first = this.unlinkedRows?.[0];
      const normalized = String(first?.targetEntityType || "").toLowerCase();
      if (normalized === "person") return "person";
      return "company";
    },
    activityEntityLabel() {
      return this.activityEntityType === "person"
        ? this.$t("ncrm.importDetails.selectPerson")
        : this.$t("ncrm.importDetails.selectCompany");
    },
    errorHeaders() {
      return [
        { title: this.$t("ncrm.importDetails.headers.row"), key: "rowNumber" },
        { title: this.$t("ncrm.importDetails.headers.error"), key: "errorMessage" },
        { title: this.$t("ncrm.importDetails.headers.raw"), key: "raw" },
        { title: this.$t("ncrm.importDetails.headers.actions"), key: "actions", sortable: false },
      ];
    },
    errorResolveHeaders() {
      return [
        { title: this.$t("ncrm.importDetails.resolveHeaders.field"), key: "field" },
        { title: this.$t("ncrm.importDetails.resolveHeaders.incoming"), key: "value" },
      ];
    },
    resolveHeaders() {
      return [
        { title: this.$t("ncrm.importDetails.resolveHeaders.field"), key: "field" },
        { title: this.$t("ncrm.importDetails.resolveHeaders.existing"), key: "existingValue" },
        { title: this.$t("ncrm.importDetails.resolveHeaders.incoming"), key: "incomingValue" },
        { title: this.$t("ncrm.importDetails.resolveHeaders.decision"), key: "decision" },
      ];
    },
    decisionOptions() {
      const options = [
        { title: this.$t("ncrm.importDetails.decisions.keep"), value: "keep" },
        { title: this.$t("ncrm.importDetails.decisions.incoming"), value: "incoming" },
        {
          title: this.$t("ncrm.importDetails.decisions.keepFillMissing"),
          value: "keep_fill_missing",
        },
        {
          title: this.$t("ncrm.importDetails.decisions.incomingFillMissing"),
          value: "incoming_fill_missing",
        },
      ];
      if (!this.isActivityImport) {
        options.push({
          title: this.$t("ncrm.importDetails.decisions.keepBoth"),
          value: "keep_both",
        });
      }
      return options;
    },
  },
  watch: {
    activeTab() {
      if (this.activeTab === "unlinked") {
        if (this.isActivityImport) {
          this.loadActivityEntityOptions(this.activityEntityType);
        } else {
          this.loadCompanyOptions();
        }
      }
      this.fetchRows();
    },
    contactPage() {
      if (this.activeTab === "contacts") this.fetchRows();
    },
    errorPage() {
      if (this.activeTab === "errors") this.fetchRows();
    },
    conflictPage() {
      if (this.activeTab === "conflicts") this.fetchRows();
    },
    unlinkedPage() {
      if (this.activeTab === "unlinked") this.fetchRows();
    },
    unlinkedSearch() {
      this.unlinkedPage = 1;
      if (this.activeTab === "unlinked") this.fetchRows();
    },
    itemsPerPage() {
      this.contactPage = 1;
      this.unlinkedPage = 1;
      this.conflictPage = 1;
      this.errorPage = 1;
      this.fetchRows();
    },
  },
  mounted() {
    this.fetchBatch();
    this.fetchRows();
  },
  methods: {
    statusMeta(status) {
      if (!status) {
        return { color: "neutral", variant: "tonal" };
      }
      const value = String(status).toLowerCase();
      if (value.includes("rolled_back")) {
        return { color: "warning", variant: "tonal" };
      }
      if (value.includes("conflict")) {
        return { color: "error", variant: "flat" };
      }
      if (value.includes("error")) {
        return { color: "error", variant: "tonal" };
      }
      if (value.includes("complete") || value.includes("success") || value.includes("updated")) {
        return { color: "success", variant: "tonal" };
      }
      if (value.includes("running")) {
        return { color: "warning", variant: "tonal" };
      }
      return { color: "neutral", variant: "tonal" };
    },
    statusColor(status) {
      return this.statusMeta(status).color;
    },
    statusVariant(status) {
      return this.statusMeta(status).variant;
    },
    formatStatus(status) {
      if (!status) return "-";
      return String(status)
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    async fetchBatch() {
      try {
        const { data } = await axios.get(`/ncrm/import/${this.batchId}`);
        this.batch = {
          ...(data || {}),
          conflict_count:
            Number(data?.conflict_count || 0) ||
            Number(data?.status_counts?.conflict || 0),
        };
        const importType = String(this.batch?.import_type || "").toLowerCase();
        if (!["person", "activity"].includes(importType) && this.activeTab === "unlinked") {
          this.activeTab = "contacts";
        } else if (this.activeTab === "unlinked") {
          this.fetchRows();
        }
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.loadBatchError");
        this.snackbar.show = true;
      }
    },
    async fetchRows() {
      this.rowsLoading = true;
      try {
        const isErrors = this.activeTab === "errors";
        const isConflicts = this.activeTab === "conflicts";
        const isUnlinked = this.activeTab === "unlinked";
        if (isUnlinked) {
          if (this.isActivityImport) {
            const { data } = await axios.get(`/ncrm/import/${this.batchId}/unlinked-activities`, {
              params: {
                page: this.unlinkedPage,
                perPage: this.itemsPerPage,
                search: this.unlinkedSearch || undefined,
              },
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            this.unlinkedRows = items.map((item) => ({
              ...item,
              selectedEntityId: null,
            }));
            this.unlinkedTotal = Number(data?.total || 0);
            await this.loadActivityEntityOptions(this.activityEntityType);
          } else {
            const { data } = await axios.get(`/ncrm/import/${this.batchId}/unlinked-persons`, {
              params: {
                page: this.unlinkedPage,
                perPage: this.itemsPerPage,
                search: this.unlinkedSearch || undefined,
              },
            });
            const items = Array.isArray(data?.items)
              ? data.items
              : [];
            this.unlinkedRows = items.map((item) => ({
              ...item,
              selectedCompanyId: null,
            }));
            this.unlinkedTotal = Number(data?.total || 0);
          }
          return;
        }

        const { data } = await axios.get(`/ncrm/import/${this.batchId}/rows`, {
          params: {
            page: isErrors ? this.errorPage : isConflicts ? this.conflictPage : this.contactPage,
            perPage: this.itemsPerPage,
            status: isErrors ? "error" : isConflicts ? "conflict" : "success,updated,skipped",
          },
        });
        if (isErrors) {
          this.errorRows = Array.isArray(data?.items) ? data.items : [];
          this.errorTotal = Number(data?.total || 0);
        } else if (isConflicts) {
          this.conflictRows = Array.isArray(data?.items) ? data.items : [];
          this.conflictTotal = Number(data?.total || 0);
        } else {
          this.contactRows = Array.isArray(data?.items) ? data.items : [];
          this.contactTotal = Number(data?.total || 0);
        }
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.loadRowsError");
        this.snackbar.show = true;
      } finally {
        this.rowsLoading = false;
      }
    },
    isConflictSelected(rowId) {
      const normalized = Number(rowId);
      if (!Number.isInteger(normalized) || normalized <= 0) return false;
      return this.selectedConflictRowIds.some((value) => Number(value) === normalized);
    },
    setConflictSelected(rowId, nextValue) {
      const normalized = Number(rowId);
      if (!Number.isInteger(normalized) || normalized <= 0) return;
      const shouldSelect = Boolean(nextValue);
      if (shouldSelect) {
        if (this.isConflictSelected(normalized)) return;
        this.selectedConflictRowIds = [...this.selectedConflictRowIds, normalized];
        return;
      }
      this.selectedConflictRowIds = this.selectedConflictRowIds.filter(
        (value) => Number(value) !== normalized
      );
    },
    toggleSelectAllConflictsOnPage(nextValue) {
      const shouldSelect = Boolean(nextValue);
      const pageIds = this.conflictPageRowIds;
      if (!pageIds.length) return;
      if (shouldSelect) {
        const next = new Set(this.selectedConflictRowIds.map((value) => Number(value)));
        pageIds.forEach((id) => next.add(id));
        this.selectedConflictRowIds = Array.from(next);
        return;
      }
      const pageIdSet = new Set(pageIds);
      this.selectedConflictRowIds = this.selectedConflictRowIds.filter(
        (value) => !pageIdSet.has(Number(value))
      );
    },
    clearConflictSelection() {
      this.selectedConflictRowIds = [];
    },
    async submitBulkResolveConflicts({ allConflicts = false } = {}) {
      if (this.bulkResolveLoading) return;
      if (!allConflicts && !this.selectedConflictCount) {
        this.snackbar.message = this.$t("ncrm.importDetails.bulkResolveSelectRows");
        this.snackbar.show = true;
        return;
      }
      if (allConflicts && !Number(this.conflictTotal || 0)) {
        return;
      }

      this.bulkResolveLoading = true;
      this.bulkResolveScope = allConflicts ? "all" : "selected";
      try {
        const payload = {
          decision: this.bulkConflictDecision,
        };
        if (allConflicts) {
          payload.allConflicts = true;
          payload.scope = "all";
        } else {
          payload.rowIds = this.selectedConflictRowIds.slice();
        }

        const { data } = await axios.post(`/ncrm/import/${this.batchId}/rows/resolve-bulk`, payload);
        const resolvedCount = Number(data?.resolvedCount || 0);
        const failedCount = Number(data?.failedCount || 0);
        this.snackbar.message =
          failedCount > 0
            ? this.$t("ncrm.importDetails.bulkResolvePartial", { resolved: resolvedCount, failed: failedCount })
            : this.$t("ncrm.importDetails.bulkResolveSuccess", { count: resolvedCount });
        this.snackbar.show = true;

        if (allConflicts) {
          this.clearConflictSelection();
        } else {
          const selectedSet = new Set(this.selectedConflictRowIds.map((value) => Number(value)));
          const failedIds = new Set(
            (Array.isArray(data?.failed) ? data.failed : [])
              .map((entry) => Number(entry?.rowId))
              .filter((value) => Number.isInteger(value) && value > 0)
          );
          this.selectedConflictRowIds = Array.from(selectedSet).filter((id) => failedIds.has(id));
        }

        await Promise.all([this.fetchBatch(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.bulkResolveError");
        this.snackbar.show = true;
      } finally {
        this.bulkResolveLoading = false;
        this.bulkResolveScope = "";
      }
    },
    applyBulkResolveSelected() {
      this.submitBulkResolveConflicts({ allConflicts: false });
    },
    applyBulkResolveAllConflicts() {
      this.submitBulkResolveConflicts({ allConflicts: true });
    },
    openRollbackDialog() {
      if (!this.canRollback) return;
      this.rollbackDialog = {
        show: true,
        loading: false,
        confirmText: "",
      };
    },
    closeRollbackDialog() {
      this.rollbackDialog = {
        show: false,
        loading: false,
        confirmText: "",
      };
    },
    async submitRollback() {
      if (!this.canRollback) return;
      if (String(this.rollbackDialog.confirmText || "").trim().toUpperCase() !== "DELETE") {
        this.snackbar.message = this.$t("ncrm.importDetails.confirmDeleteError");
        this.snackbar.show = true;
        return;
      }
      this.rollbackDialog.loading = true;
      try {
        const { data } = await axios.post(`/ncrm/import/${this.batchId}/rollback`, {
          confirmText: this.rollbackDialog.confirmText,
        });
        this.snackbar.message = this.$t("ncrm.importDetails.rollbackSuccess", {
          created: data?.revertedCreated || 0,
          updated: data?.revertedUpdated || 0,
        });
        this.snackbar.show = true;
        this.closeRollbackDialog();
        this.activeTab = "contacts";
        await Promise.all([this.fetchBatch(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.rollbackError");
        this.snackbar.show = true;
      } finally {
        this.rollbackDialog.loading = false;
      }
    },
    async loadCompanyOptions() {
      if (this.companyOptionsLoading) return;
      this.companyOptionsLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "company",
            perPage: -1,
          },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        this.companyOptions = items.map((item) => ({
          title: item.name,
          value: item.id,
        }));
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.loadCompaniesError");
        this.snackbar.show = true;
      } finally {
        this.companyOptionsLoading = false;
      }
    },
    async loadActivityEntityOptions(entityType = "company") {
      if (this.activityEntityOptionsLoading) return;
      this.activityEntityOptionsLoading = true;
      try {
        const type = String(entityType || "company").toLowerCase() === "person" ? "person" : "company";
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type,
            perPage: -1,
          },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        this.activityEntityOptions = items.map((item) => {
          if (type === "person") {
            const title = [item.first_name, item.last_name].filter(Boolean).join(" ").trim() || `#${item.id}`;
            return {
              title,
              value: item.id,
            };
          }
          return {
            title: item.name,
            value: item.id,
          };
        });
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.loadEntitiesError");
        this.snackbar.show = true;
      } finally {
        this.activityEntityOptionsLoading = false;
      }
    },
    async linkUnlinkedPerson(item) {
      if (!item?.personId || !item?.selectedCompanyId) return;
      try {
        await axios.post(`/ncrm/import/${this.batchId}/unlinked-persons/${item.personId}/link`, {
          ncrmCompanyId: item.selectedCompanyId,
        });
        this.snackbar.message = this.$t("ncrm.importDetails.linkSuccess");
        this.snackbar.show = true;
        await this.fetchRows();
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.linkError");
        this.snackbar.show = true;
      }
    },
    openCreateCompanyDialog(item) {
      this.createCompanyDialog = {
        show: true,
        loading: false,
        personId: item?.personId || null,
        name: item?.name || "",
      };
    },
    closeCreateCompanyDialog() {
      this.createCompanyDialog = {
        show: false,
        loading: false,
        personId: null,
        name: "",
      };
    },
    async createCompanyForPerson() {
      if (!this.createCompanyDialog.personId) return;
      if (!String(this.createCompanyDialog.name || "").trim()) {
        this.snackbar.message = this.$t("ncrm.importDetails.companyNameRequired");
        this.snackbar.show = true;
        return;
      }
      this.createCompanyDialog.loading = true;
      try {
        await axios.post(
          `/ncrm/import/${this.batchId}/unlinked-persons/${this.createCompanyDialog.personId}/create-company`,
          {
            name: this.createCompanyDialog.name,
          }
        );
        this.snackbar.message = this.$t("ncrm.importDetails.createCompanySuccess");
        this.snackbar.show = true;
        this.closeCreateCompanyDialog();
        await Promise.all([this.loadCompanyOptions(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.createCompanyError");
        this.snackbar.show = true;
      } finally {
        this.createCompanyDialog.loading = false;
      }
    },
    async skipUnlinkedPerson(item) {
      if (!item?.personId) return;
      try {
        await axios.post(`/ncrm/import/${this.batchId}/unlinked-persons/${item.personId}/skip`);
        this.snackbar.message = this.$t("ncrm.importDetails.skipSuccess");
        this.snackbar.show = true;
        await this.fetchRows();
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.skipError");
        this.snackbar.show = true;
      }
    },
    async assignUnlinkedActivity(item) {
      if (!item?.rowId || !item?.selectedEntityId) return;
      try {
        await axios.post(`/ncrm/import/${this.batchId}/unlinked-activities/${item.rowId}/assign`, {
          entityId: item.selectedEntityId,
        });
        this.snackbar.message = this.$t("ncrm.importDetails.assignSuccess");
        this.snackbar.show = true;
        await Promise.all([this.fetchBatch(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.assignError");
        this.snackbar.show = true;
      }
    },
    async skipUnlinkedActivity(item) {
      if (!item?.rowId) return;
      try {
        await axios.post(`/ncrm/import/${this.batchId}/unlinked-activities/${item.rowId}/skip`);
        this.snackbar.message = this.$t("ncrm.importDetails.skipActivitySuccess");
        this.snackbar.show = true;
        await this.fetchRows();
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.skipActivityError");
        this.snackbar.show = true;
      }
    },
    resolveMappedField(row, key) {
      return row?.rawData?.mapped?.[key] || "-";
    },
    resolveDisplayName(row) {
      const mapped = row?.rawData?.mapped || {};
      if (mapped.name) return mapped.name;
      const full = [mapped.first_name, mapped.last_name].filter(Boolean).join(" ").trim();
      return full || mapped.last_name || "-";
    },
    resolveActivityContent(row) {
      const mapped = row?.rawData?.mapped || {};
      const content = mapped.content || mapped.title || "";
      return String(content || "").trim() || "-";
    },
    resolveActivityEntity(row) {
      const entityMatch = row?.rawData?.entityMatch || {};
      const entityName = String(entityMatch.resolvedEntityName || "").trim();
      if (entityName) return entityName;
      const entityType = entityMatch.targetEntityType || "-";
      const entityId = entityMatch.resolvedEntityId || row?.rawData?.mapped?.entity_id || "-";
      return `${entityType} #${entityId}`;
    },
    resolveActivityDedupe(row) {
      const entityMatch = row?.rawData?.entityMatch || {};
      const field = entityMatch.dedupeInternalField || row?.rawData?.importConfig?.dedupeInternalField || "";
      const value = entityMatch.dedupeSourceValue;
      if (!field && (value === null || value === undefined || value === "")) {
        return "-";
      }
      return `${field}: ${value === null || value === undefined || value === "" ? "-" : value}`;
    },
    openResolveDialog(row) {
      const fields = Array.isArray(row?.rawData?.conflictFields) ? row.rawData.conflictFields : [];
      const decisions = {};
      fields.forEach((entry) => {
        decisions[entry.field] = this.defaultConflictFieldDecision(entry);
      });
      this.resolveDialog = {
        show: true,
        loading: false,
        rowId: row?.id || null,
        fields,
        decisions,
        entityContext: this.resolveConflictEntityContext(row),
        matchSummary: this.resolveConflictMatchSummary(row),
      };
    },
    closeResolveDialog() {
      this.resolveDialog = {
        show: false,
        loading: false,
        rowId: null,
        fields: [],
        decisions: {},
        entityContext: null,
        matchSummary: "",
      };
    },
    buildErrorResolveMappedFields(rawData) {
      const mapped = rawData?.mapped && typeof rawData.mapped === "object" ? rawData.mapped : null;
      if (!mapped || Array.isArray(mapped)) return [];
      return Object.entries(mapped)
        .filter(([field]) => String(field || "").trim().length > 0)
        .map(([field, value]) => ({ field, value }));
    },
    resolveErrorDialogEntitySummary(row) {
      const entityType = String(row?.rawData?.entityType || this.batch?.import_type || "").trim().toLowerCase();
      if (!["company", "person"].includes(entityType)) return null;
      const importedName = this.resolveConflictEntityDisplayName(entityType, row?.rawData?.mapped);
      if (!importedName) return null;
      const entityLabel = entityType === "person"
        ? this.$t("ncrm.importDetails.entityLabels.person")
        : this.$t("ncrm.importDetails.entityLabels.company");
      return {
        label: `${this.$t("ncrm.importDetails.errorResolveEntityImported", { entity: entityLabel })}: `,
        value: importedName,
      };
    },
    openErrorResolveDialog(row) {
      const rawData = row?.rawData && typeof row.rawData === "object" ? row.rawData : null;
      this.errorResolveDialog = {
        show: true,
        loading: false,
        rowId: row?.id || null,
        rowNumber: row?.rowNumber || null,
        errorMessage: row?.errorMessage || "",
        entitySummary: this.resolveErrorDialogEntitySummary(row),
        mappedFields: this.buildErrorResolveMappedFields(rawData),
        rawPreview: this.stringifyRaw(rawData?.raw || rawData),
      };
    },
    closeErrorResolveDialog() {
      this.errorResolveDialog = {
        show: false,
        loading: false,
        rowId: null,
        rowNumber: null,
        errorMessage: "",
        entitySummary: null,
        mappedFields: [],
        rawPreview: "",
      };
    },
    buildErrorResolveMappedPayload() {
      const rows = Array.isArray(this.errorResolveDialog?.mappedFields) ? this.errorResolveDialog.mappedFields : [];
      return rows.reduce((acc, entry) => {
        const field = String(entry?.field || "").trim();
        if (!field) return acc;
        acc[field] = entry?.value ?? "";
        return acc;
      }, {});
    },
    async submitErrorRetry() {
      if (!this.errorResolveDialog.rowId) return;
      if (this.errorResolveDialog.loading) return;

      this.errorResolveDialog.loading = true;
      try {
        const payload = {
          mapped: this.buildErrorResolveMappedPayload(),
        };
        const { data } = await axios.post(
          `/ncrm/import/${this.batchId}/rows/${this.errorResolveDialog.rowId}/retry-error`,
          payload
        );
        const nextStatus = String(data?.status || "").toLowerCase();

        if (nextStatus === "error") {
          const nextRawData = data?.rawData && typeof data.rawData === "object" ? data.rawData : null;
          this.errorResolveDialog = {
            ...this.errorResolveDialog,
            loading: false,
            errorMessage: data?.errorMessage || this.errorResolveDialog.errorMessage,
            entitySummary: this.resolveErrorDialogEntitySummary({ rawData: nextRawData }),
            mappedFields: this.buildErrorResolveMappedFields(nextRawData),
            rawPreview: this.stringifyRaw(nextRawData?.raw || nextRawData),
          };
          this.snackbar.message = this.$t("ncrm.importDetails.retryStillError");
          this.snackbar.show = true;
          await Promise.all([this.fetchBatch(), this.fetchRows()]);
          return;
        }

        if (nextStatus === "conflict") {
          this.snackbar.message = this.$t("ncrm.importDetails.retryMovedToConflicts");
        } else {
          this.snackbar.message = this.$t("ncrm.importDetails.retrySuccess");
        }
        this.snackbar.show = true;
        this.closeErrorResolveDialog();
        await Promise.all([this.fetchBatch(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.retryError");
        this.snackbar.show = true;
      } finally {
        if (this.errorResolveDialog.show) {
          this.errorResolveDialog.loading = false;
        }
      }
    },
    async submitResolve() {
      if (!this.resolveDialog.rowId) return;
      this.resolveDialog.loading = true;
      try {
        await axios.post(`/ncrm/import/${this.batchId}/rows/${this.resolveDialog.rowId}/resolve`, {
          decisions: this.resolveDialog.decisions,
        });
        this.selectedConflictRowIds = this.selectedConflictRowIds.filter(
          (value) => Number(value) !== Number(this.resolveDialog.rowId)
        );
        this.snackbar.message = this.$t("ncrm.importDetails.resolveSuccess");
        this.snackbar.show = true;
        this.closeResolveDialog();
        await Promise.all([this.fetchBatch(), this.fetchRows()]);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.resolveError");
        this.snackbar.show = true;
      } finally {
        this.resolveDialog.loading = false;
      }
    },
    humanizeField(field) {
      const map = {
        name: this.$t("ncrm.importWizard.fields.companyName"),
        address_text: this.$t("ncrm.importWizard.fields.addressText"), country: this.$t("ncrm.importWizard.fields.country"),
        business_category: this.$t("ncrm.importWizard.fields.businessCategory"),
        business_is_active: this.$t("ncrm.contacts.details.businessStatus.fieldLabel"),
        source: this.$t("ncrm.importWizard.fields.source"),
        first_name: this.$t("ncrm.importWizard.fields.firstName"),
        last_name: this.$t("ncrm.importWizard.fields.lastName"),
        email_primary: this.$t("ncrm.importWizard.fields.emailPrimary"),
        email_secondary: this.$t("ncrm.importWizard.fields.emailSecondary"),
        email_tertiary: this.$t("ncrm.importWizard.fields.emailTertiary"),
        phone_primary: this.$t("ncrm.importWizard.fields.phonePrimary"),
        phone_secondary: this.$t("ncrm.importWizard.fields.phoneSecondary"),
        phone_tertiary: this.$t("ncrm.importWizard.fields.phoneTertiary"),
        content: this.$t("ncrm.importWizard.fields.content"),
        title: this.$t("ncrm.importWizard.fields.title"),
        happened_at: this.$t("ncrm.importWizard.fields.happenedAt"),
        due_at: this.$t("ncrm.importWizard.fields.dueAt"),
        legacy_source: this.$t("ncrm.importWizard.fields.legacySource"),
        legacy_author_name: this.$t("ncrm.importWizard.fields.legacyAuthorName"),
        owner_user_id: this.$t("ncrm.importWizard.fields.ownerUserId"),
        legacy_payload: this.$t("ncrm.importWizard.fields.legacyPayload"),
        type_payload: this.$t("ncrm.importWizard.fields.typePayload"),
      };
      return map[field] || field;
    },
    isBlankConflictFieldValue(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === "string") return value.trim() === "";
      return false;
    },
    defaultConflictFieldDecision(entry) {
      const existingBlank = this.isBlankConflictFieldValue(entry?.existingValue);
      const incomingBlank = this.isBlankConflictFieldValue(entry?.incomingValue);
      if (existingBlank !== incomingBlank) {
        // Safer default for one-sided blanks: don't wipe existing non-empty values with blank import.
        return "incoming_fill_missing";
      }
      return "incoming";
    },
    resolveConflictEntityDisplayName(entityType, snapshot) {
      const normalizedType = String(entityType || "").trim().toLowerCase();
      const data = snapshot && typeof snapshot === "object" ? snapshot : null;
      if (!data) return "";

      if (normalizedType === "company") {
        return String(
          data.name ||
          data.legal_name ||
          data.email_primary ||
          data.phone_primary ||
          ""
        ).trim();
      }

      if (normalizedType === "person") {
        const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ").trim();
        return String(
          fullName ||
          data.last_name ||
          data.first_name ||
          data.email_primary ||
          data.phone_primary ||
          ""
        ).trim();
      }

      return "";
    },
    resolveConflictEntityContext(row) {
      const entityType = String(row?.rawData?.entityType || this.batch?.import_type || "").trim().toLowerCase();
      if (!["company", "person"].includes(entityType)) return null;

      const entityLabel = entityType === "person"
        ? this.$t("ncrm.importDetails.entityLabels.person")
        : this.$t("ncrm.importDetails.entityLabels.company");
      const existingName = this.resolveConflictEntityDisplayName(entityType, row?.rawData?.existing);
      const importedName = this.resolveConflictEntityDisplayName(entityType, row?.rawData?.mapped);

      if (!existingName && !importedName) return null;

      const existingLabel = this.$t("ncrm.importDetails.resolveEntityExisting", { entity: entityLabel });
      const importedLabel = this.$t("ncrm.importDetails.resolveEntityImported", { entity: entityLabel });

      return {
        entityType,
        existingName: existingName || this.$t("ncrm.importDetails.unknownEntityName"),
        importedName: importedName || "",
        existingLabel: `${existingLabel}: `,
        importedLabel: `${importedLabel}: `,
        showImported: Boolean(importedName && importedName !== existingName),
      };
    },
    inferDefaultAutoMatchMetaFromRow(row) {
      const entityMatch = row?.rawData?.entityMatch || {};
      const strategy = String(entityMatch?.strategy || "").trim().toLowerCase();
      if (!["default_auto", "configured_fallback_default_missing_source"].includes(strategy)) {
        return null;
      }

      const mapped = row?.rawData?.mapped;
      const existing = row?.rawData?.existing;
      if (!mapped || !existing) return null;

      const importType = String(row?.rawData?.entityType || this.batch?.import_type || "").trim().toLowerCase();
      const candidateFieldsByType = {
        company: ["email_primary", "phone_primary", "vat_number", "name"],
        person: ["old_reference", "email_primary", "phone_primary"],
      };
      const candidates = candidateFieldsByType[importType] || [];

      const normalizeComparable = (value) => {
        if (value === null || value === undefined || value === "") return null;
        return String(value);
      };

      for (const field of candidates) {
        const incomingValue = normalizeComparable(mapped?.[field]);
        const existingValue = normalizeComparable(existing?.[field]);
        if (incomingValue === null || existingValue === null) continue;
        if (incomingValue !== existingValue) continue;
        return {
          matchedByField: field,
          matchedByValue: incomingValue,
        };
      }
      return null;
    },
    resolveConflictMatchSummary(row) {
      const entityMatch = row?.rawData?.entityMatch || {};
      const strategy = String(entityMatch?.strategy || "").trim();
      const inferredMatchMeta = this.inferDefaultAutoMatchMetaFromRow(row);
      const matchedByField = String(
        entityMatch?.matchedByField || inferredMatchMeta?.matchedByField || entityMatch?.internalField || ""
      ).trim();
      const matchedByValue =
        entityMatch && Object.prototype.hasOwnProperty.call(entityMatch, "matchedByValue")
          ? entityMatch.matchedByValue
          : inferredMatchMeta && Object.prototype.hasOwnProperty.call(inferredMatchMeta, "matchedByValue")
            ? inferredMatchMeta.matchedByValue
          : entityMatch?.sourceValue;

      if (!matchedByField && !strategy) return "";

      const label = this.$t("ncrm.importDetails.matchedBy");
      const fieldLabel = matchedByField ? this.humanizeField(matchedByField) : "";
      const hasValue = !(matchedByValue === null || matchedByValue === undefined || matchedByValue === "");
      const safeValue = hasValue ? this.formatCellValue(matchedByValue) : "";
      const strategySuffix = strategy ? ` (${strategy})` : "";

      if (fieldLabel && hasValue) {
        return `${label}: ${fieldLabel} = ${safeValue}${strategySuffix}`;
      }
      if (fieldLabel) {
        return `${label}: ${fieldLabel}${strategySuffix}`;
      }
      return `${label}: ${strategy}`;
    },
    formatCellValue(value) {
      if (value === null || value === undefined || value === "") return "-";
      return String(value);
    },
    stringifyRaw(raw) {
      if (!raw) return "-";
      if (typeof raw === "string") return raw;
      try {
        return JSON.stringify(raw);
      } catch (_) {
        return String(raw);
      }
    },
    async downloadErrorsCsv() {
      try {
        const response = await axios.get(`/ncrm/import/${this.batchId}/errors.csv`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `import-errors-${this.batchId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importDetails.downloadError");
        this.snackbar.show = true;
      }
    },
    goBack() {
      this.$router.push("/ncrm/data-integrations");
    },
  },
};
</script>

<style scoped>
.ncrm-import-details {
  text-align: left;
}

.ncrm-unlinked-search {
  min-width: 260px;
  max-width: 360px;
}

.ncrm-import-details__bulk-select {
  min-width: 220px;
}

.ncrm-import-details__match-summary {
  color: rgba(var(--v-theme-on-surface), 0.78);
}

.ncrm-import-details__entity-summary,
.ncrm-import-details__entity-summary-secondary {
  color: rgba(var(--v-theme-on-surface), 0.88);
}

.ncrm-import-details__raw-preview {
  max-height: 260px;
  overflow: auto;
}

.ncrm-import-details__raw-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: var(--app-font-family-mono);
  font-size: 12px;
  line-height: 1.45;
}
</style>
